/**
 * The Publish dialog — its confirmation (Story 16.1), and what a refused
 * publish becomes (Story 25.4).
 *
 * Visual Reference: `design/screens/editor-build.html`, states `publish`,
 * `publish-held`, `publish-blocked` and `publish-attest`, at 1440 and 375.
 *
 * A RESULT REPLACES THE DIALOG'S CONTENTS; no second dialog stacks
 * (DESIGN-SYSTEM §4.17). So this is one Modal whose words follow the answer:
 * `outcome` is null while it asks, and becomes what the refusal's code says —
 * `publish-outcome.ts` decides that, from the code alone.
 *
 * NO CLOSE MARK in any of the four, because none of the four captures draws
 * one: each dialog's own button is its way out, and Escape and the backdrop
 * still dismiss — Story 24.3's reading of the Profile dialogs, which draw none
 * either.
 */
import { useState } from 'react'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { COMPOSED, DRAWN, type Vertical } from './publish-copy'
import type { Evidence, Outcome } from './publish-outcome'

/** "23 September 2026 at 10:42" — the drawing's own shape. */
const recordedAt = (at: number): string => {
  const date = new Date(at)
  return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} at ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

export function PublishDialog({
  name,
  status,
  org,
  person,
  busy,
  error,
  outcome,
  onClose,
  onConfirm,
}: {
  name: string
  /** The funnel's status. `published` means an earlier version is live. */
  status: string
  /** The organisation's name — the attestation is theirs to make. */
  org: string
  /** The person pressing, as the attestation records them. */
  person: string
  busy: boolean
  error: string | null
  outcome: Outcome | null
  onClose: () => void
  /** Publish; with the verticals the person confirms, when attesting. */
  onConfirm: (attest?: Vertical[]) => void
}) {
  const live = status === 'published'
  const still = live ? ` ${COMPOSED.stillLive}` : ''

  if (outcome?.kind === 'held') {
    return (
      <Modal
        title={DRAWN.heldTitle(name)}
        subtitle={<Line>{DRAWN.heldLine(outcome.intents)}</Line>}
        onClose={onClose}
        closeMark={false}
        balance
        actions={<Button onClick={onClose}>{DRAWN.close}</Button>}
      >
        <Body>
          <EvidenceRows rows={outcome.evidence} tone="review" />
          {/*
            `See what reviewers check` is drawn beneath this line and is NOT
            rendered: what reviewers check is PRD §7.7's operator criteria, which
            are M6, and nothing in the product states them yet — the same reason
            `Funnels.tsx` withheld the card's link at M1 (DECISION-016). A link
            to nowhere is worse than none (BACKLOG, 2026-09-28).
          */}
          <p className="m-0 max-w-mdx-measure-56 text-pretty text-mdx-13 text-mdx-slate">
            {DRAWN.heldNext}
            {still}
          </p>
        </Body>
      </Modal>
    )
  }

  if (outcome?.kind === 'blocked') {
    return (
      <Modal
        title={DRAWN.blockedTitle(name)}
        subtitle={<Line>{DRAWN.blockedLine(outcome.verticals)}</Line>}
        onClose={onClose}
        closeMark={false}
        balance
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              {DRAWN.keepEditing}
            </Button>
            <Button onClick={() => window.location.assign('/billing')}>{DRAWN.seePlans}</Button>
          </>
        }
      >
        <Body>
          <EvidenceRows rows={outcome.evidence} tone="nurture" />
          <p className="m-0 max-w-mdx-measure-56 text-pretty text-mdx-13 text-mdx-slate">
            {DRAWN.blockedNext(outcome.verticals)}
            {still}
          </p>
        </Body>
      </Modal>
    )
  }

  if (outcome?.kind === 'attest') {
    return (
      <Attest
        name={name}
        org={org}
        person={person}
        verticals={outcome.verticals}
        evidence={outcome.evidence}
        busy={busy}
        error={error}
        onClose={onClose}
        onConfirm={() => onConfirm(outcome.verticals)}
      />
    )
  }

  return (
    <Modal
      title={`Publish ${name}?`}
      onClose={onClose}
      closeMark={false}
      balance
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Not yet
          </Button>
          <Button loading={busy ? 'Publishing' : undefined} onClick={() => onConfirm()}>
            Publish funnel
          </Button>
        </>
      }
    >
      <p className="m-0 max-w-mdx-measure-52 text-mdx-15 leading-mdx-body text-mdx-slate">
        {live ? (
          <>
            <span className="font-mdx-semibold text-mdx-ink">It is already live.</span> This
            replaces the running version on every site the embed code is pasted on, straight
            away.
          </>
        ) : (
          <>
            <span className="font-mdx-semibold text-mdx-ink">It goes live straight away.</span>{' '}
            The form starts answering on every site the embed code is pasted on.
          </>
        )}
      </p>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
        What you publish is a snapshot. Later edits stay a draft until you publish again, and
        leads already collected are not affected.
      </p>
      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

/**
 * Confirm your compliance basis — PRD §5.11's "explicit attestation of the
 * customer's own compliance basis, logged with timestamp and user ID".
 *
 * READ BEFORE IT IS CONFIRMED: the button stays disabled until the box is
 * ticked, and the box is never ticked for the person. What it posts is exactly
 * what the refusal listed — `attest` names each vertical still to confirm — so a
 * funnel edited in another tab into a new vertical is refused again, naming it,
 * rather than published on a confirmation that did not cover it.
 */
function Attest({
  name,
  org,
  person,
  verticals,
  evidence,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  name: string
  org: string
  person: string
  verticals: Vertical[]
  evidence: Evidence[]
  busy: boolean
  error: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  const [confirmed, setConfirmed] = useState(false)
  return (
    <Modal
      title={DRAWN.attestTitle}
      subtitle={<Line>{DRAWN.attestLine(name, verticals)}</Line>}
      onClose={onClose}
      closeMark={false}
      balance
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {DRAWN.notYet}
          </Button>
          <Button disabled={!confirmed} loading={busy ? 'Publishing' : undefined} onClick={onConfirm}>
            {DRAWN.confirmAndPublish}
          </Button>
        </>
      }
    >
      <Body>
        <EvidenceRows rows={evidence} tone="nurture" />
        <div className="flex flex-col gap-mdx-6">
          <label className="flex cursor-pointer items-start gap-mdx-10">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              className="mx-0 mb-0 mt-mdx-4 size-mdx-18 shrink-0 cursor-pointer accent-mdx-brand"
            />
            <span className="max-w-mdx-measure-52 text-pretty text-mdx-15">{DRAWN.attestBox(org, verticals)}</span>
          </label>
          <span className="pl-mdx-28 text-mdx-13 text-mdx-slate">{DRAWN.attestRecord(person, recordedAt(Date.now()))}</span>
        </div>
        {error ? (
          <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
            {error}
          </p>
        ) : null}
      </Body>
    </Modal>
  )
}

/** The dialog's 15px line, at the drawn measure — `max-width: 56ch; text-wrap: pretty`. */
function Line({ children }: { children: string }) {
  return <span className="block max-w-mdx-measure-56 text-pretty">{children}</span>
}

/**
 * The drawn body's own 16px stack. The Modal spaces its sections 20px apart,
 * which the older dialogs were compared against; M3's draw 16 between the rows
 * and the paragraph beneath them, so the body keeps its own rhythm inside it.
 */
function Body({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-mdx-16">{children}</div>
}

/**
 * What was found, and where — the brief's evidence row on `ground`, a 26px
 * square on the review fill ("~", a hold) or the neutral one ("–", a block),
 * then the step in 600 and the question's own words in slate.
 */
function EvidenceRows({ rows, tone }: { rows: Evidence[]; tone: 'review' | 'nurture' }) {
  return (
    <>
      {rows.map((row) => (
        <div
          key={`${row.where}\n${row.text}`}
          className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-6 rounded-mdx-control bg-mdx-ground px-mdx-12 py-mdx-10"
        >
          <span
            aria-hidden="true"
            className={`flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold text-mdx-white ${
              tone === 'review' ? 'bg-mdx-review' : 'bg-mdx-nurture'
            }`}
          >
            {tone === 'review' ? '~' : '–'}
          </span>
          <span className="shrink-0 text-mdx-15 font-mdx-semibold">{row.where}</span>
          <span className="min-w-0 flex-1 basis-mdx-row-identity text-mdx-15 text-mdx-slate">
            {DRAWN.quoted(row.text)}
          </span>
        </div>
      ))}
    </>
  )
}
